import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { getOptimizedImageUrl } from '../lib/image'
import { getCompanyInfo, getCmsCollection } from '../lib/cms'

const DEFAULT_LOGO_URL = 'https://assets.auxodev.dk/assets/a2a4e36d-8b70-41e2-a1fb-0085c6465cf9/img-2863-4.jpeg'

export default function Footer() {
  const [info, setInfo] = useState(null)
  const [services, setServices] = useState([])

  useEffect(() => {
    async function loadFooterData() {
      try {
        const [comp, serv] = await Promise.all([
          getCompanyInfo(),
          getCmsCollection('tjenester'),
        ])
        if (comp) setInfo(comp)
        if (Array.isArray(serv) && serv.length > 0) {
          const sorted = serv.sort((a, b) => (a.order || 0) - (b.order || 0))
          setServices(sorted.slice(0, 6))
        }
      } catch (err) {
        console.error('Error loading footer CMS data:', err)
      }
    }
    loadFooterData()
  }, [])

  const logoUrl = info?.logo?.[0] || DEFAULT_LOGO_URL
  const ctaBadge = info?.footer_cta_badge || 'Klar til dit næste byggeprojekt?'
  const ctaHeading = info?.footer_cta_heading || 'Få et uforpligtende tilbud på dit gravearbejde'
  const ctaText = info?.footer_cta_text || 'Vi leverer hurtige, præcise og pålidelige entreprenørløsninger på hele Fyn med moderne maskiner.'
  const bio = info?.footer_bio || 'Lyn Entreprenør Aps er en professionel virksomhed på Fyn, der specialiserer sig i jordarbejde og levering af maskiner og udstyr til byggeri. Vi tilbyder effektive og pålidelige løsninger til vores kunder.'
  const phone = info?.phone || '55 23 69 32'
  const phoneClean = phone.replace(/[^0-9+]/g, '')
  const email = info?.email_primary || 'jc@lyn-ent.dk'
  const facebook = info?.facebook_url || 'https://www.facebook.com/LynEntrepreneor'
  const address = info?.address || 'Krogsbjerg 1'
  const city = info?.postal_and_city || '5683 Haarby'
  const cvr = info?.cvr || '46264126'

  return (
    <footer className="bg-[#000000] text-slate-300">
      {/* Top CTA Banner */}
      <div className="bg-[#bf0000] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-red-200">
              {ctaBadge}
            </span>
            <h3 className="text-2xl sm:text-3xl font-extrabold text-white mt-1">
              {ctaHeading}
            </h3>
            <p className="text-red-100 text-sm mt-1 max-w-xl">
              {ctaText}
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3">
            <a
              href={`tel:${phoneClean}`}
              className="inline-flex items-center gap-2 rounded-xl bg-white px-6 py-3.5 text-sm font-bold text-slate-900 shadow-lg transition duration-200 hover:bg-slate-100 whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-red-600" aria-hidden="true" />
              <span>Ring {phone}</span>
            </a>
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900/80 px-6 py-3.5 text-sm font-bold text-white border border-white/20 transition duration-200 hover:bg-slate-900 whitespace-nowrap"
            >
              <i className="fa-solid fa-envelope" aria-hidden="true" />
              <span>Send en besked</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Main Footer Links */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
          {/* Company Bio */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center gap-3">
              <div className="inline-flex items-center">
                <img
                  src={getOptimizedImageUrl(logoUrl, { width: 560, height: 280, quality: 95 })}
                  alt="Lyn Entreprenør ApS Logo"
                  className="h-16 sm:h-18 w-auto object-contain"
                />
              </div>
            </div>

            <p className="text-slate-400 text-sm leading-relaxed max-w-md">
              {bio}
            </p>

            <div className="pt-2 flex items-center gap-4">
              <a
                href={facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Lyn Entreprenør på Facebook"
                className="text-slate-400 hover:text-white transition text-lg"
              >
                <i className="fa-brands fa-facebook-f" aria-hidden="true" />
              </a>
              <a
                href={`mailto:${email}`}
                aria-label="Send email"
                className="text-slate-400 hover:text-white transition text-lg"
              >
                <i className="fa-solid fa-envelope" aria-hidden="true" />
              </a>
              <a
                href={`tel:${phoneClean}`}
                aria-label="Ring op"
                className="text-slate-400 hover:text-white transition text-lg"
              >
                <i className="fa-solid fa-phone" aria-hidden="true" />
              </a>
            </div>
          </div>

          {/* Quick links */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-wider text-white mb-4">
              Navigation
            </h4>
            <ul className="space-y-2.5 text-sm">
              <li>
                <Link to="/" className="text-slate-400 hover:text-white transition">Hjem</Link>
              </li>
              <li>
                <Link to="/tjenester" className="text-slate-400 hover:text-white transition">Tjenester</Link>
              </li>
              <li>
                <Link to="/billedgalleri" className="text-slate-400 hover:text-white transition">Billedgalleri</Link>
              </li>
              <li>
                <Link to="/prisliste" className="text-slate-400 hover:text-white transition">Prisliste</Link>
              </li>
              <li>
                <Link to="/om-os" className="text-slate-400 hover:text-white transition">Om os</Link>
              </li>
              <li>
                <Link to="/kontakt" className="text-slate-400 hover:text-white transition">Kontakt</Link>
              </li>
            </ul>
          </div>

          {/* Ydelser Links */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-wider text-white mb-4">
              Ydelser
            </h4>
            <ul className="space-y-2.5 text-sm">
              {services.length > 0 ? (
                services.map((s) => {
                  const sSlug = typeof s.slug === 'object' ? s.slug?.da : s.slug
                  const cleanSlug = sSlug ? sSlug.replace(/^tjeneste-|^service-/, '') : ''
                  return (
                    <li key={s.id || s.title}>
                      <Link to={`/tjenester/${cleanSlug}`} className="text-slate-400 hover:text-white transition">
                        {s.title}
                      </Link>
                    </li>
                  )
                })
              ) : (
                <>
                  <li><Link to="/tjenester/jordboring" className="text-slate-400 hover:text-white transition">Jordboring</Link></li>
                  <li><Link to="/tjenester/grofterensning" className="text-slate-400 hover:text-white transition">Grøfterensning</Link></li>
                  <li><Link to="/tjenester/belaegningsarbejde" className="text-slate-400 hover:text-white transition">Belægningsarbejde</Link></li>
                  <li><Link to="/tjenester/kloakarbejde" className="text-slate-400 hover:text-white transition">Kloakarbejde</Link></li>
                  <li><Link to="/tjenester/anlaegsarbejde" className="text-slate-400 hover:text-white transition">Anlægsarbejde</Link></li>
                  <li><Link to="/tjenester/draenarbejde" className="text-slate-400 hover:text-white transition">Drænarbejde</Link></li>
                </>
              )}
            </ul>
          </div>

          {/* Contact Details */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-wider text-white mb-4">
              Virksomhed
            </h4>
            <div className="space-y-3 text-sm text-slate-400">
              <div>
                <strong className="text-white block font-medium">{info?.company_name || 'Lyn Entreprenør ApS'}</strong>
                <span className="block text-slate-400">CVR: {cvr}</span>
              </div>

              <div className="flex items-start gap-2.5 pt-1">
                <i className="fa-solid fa-location-dot text-red-500 mt-1" aria-hidden="true" />
                <span>{address}<br />{city}, Danmark</span>
              </div>

              <div className="flex items-center gap-2.5 pt-1">
                <i className="fa-solid fa-phone text-red-500" aria-hidden="true" />
                <a href={`tel:${phoneClean}`} className="text-slate-300 hover:text-white transition whitespace-nowrap">
                  {phone}
                </a>
              </div>

              <div className="flex items-center gap-2.5 pt-1">
                <i className="fa-solid fa-envelope text-red-500" aria-hidden="true" />
                <a href={`mailto:${email}`} className="text-slate-300 hover:text-white transition whitespace-nowrap">
                  {email}
                </a>
              </div>

              <div className="flex items-center gap-2.5 pt-1">
                <i className="fa-brands fa-facebook text-red-500" aria-hidden="true" />
                <a
                  href={facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-300 hover:text-white transition truncate"
                >
                  Facebook side
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom copyright row */}
        <div className="mt-14 pt-8 border-t border-slate-800/80 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500">
          <p>© {new Date().getFullYear()} Lyn Entreprenør ApS. Alle rettigheder forbeholdes. CVR: 46264126.</p>
          <p className="flex items-center gap-4">
            <span className="text-slate-400">Kvalitetsbevidst jordarbejde på Fyn</span>
          </p>
        </div>
      </div>
    </footer>
  )
}
